import React, { useState, useEffect, useRef } from 'react';
import { useField, useFormApi } from 'informed';
import { useTranslation } from 'react-i18next';

import OtpInputController from '../OtpInput/OtpInput';
import { customToast } from '../ToastUtils/ToastUtils';

// OTP validation: exactly 6 digits
const isValidSixDigitOTP = (value: string): boolean => /^[0-9]{6}$/.test(value);

interface InputFieldProps {
  field: string;
  label: string;
  validationSchemaObject: any;
  touched: boolean;
  error: string;
  disabled?: boolean;
  apiStatus?: number;
  labelStyles?: string;
  otpContainerStyles?: string;
  inputClassName?: string;
  inputFocusClassName?: string;
  errorClassName?: string;
  errorMessageStyles?: string;
  numInputs?: number;
  onHandleChange?: (value: string) => void;
  enableAutoVerify?: boolean;
  onVerifyOTP?: (otp: string) => void;
  onClearError?: () => void;
}

export const InformedOTPFieldWithBoxes = React.memo(
  (props: InputFieldProps) => {
    const { t } = useTranslation();
    const {
      validationSchemaObject,
      field,
      touched,
      error,
      disabled,
      apiStatus,
      labelStyles,
      label,
      otpContainerStyles,
      inputClassName,
      inputFocusClassName,
      errorClassName,
      errorMessageStyles,
      numInputs,
      onHandleChange,
      enableAutoVerify = false,
      onVerifyOTP,
      onClearError,
    } = props;

    const { fieldState, render, informed } = useField({
      field,
      validationSchema: validationSchemaObject.fields[field],
      error: error,
      touched: touched,
    });
    const { value } = fieldState;
    const { onChange, onBlur } = informed;
    const formApi = useFormApi();
    const isNotValid = touched && error ? true : false;

    const [isFocus, setFocus] = useState(true);
    const previousErrorRef = useRef<string | undefined>(error);
    const isAutoVerifyingRef = useRef<boolean>(false);

    const showToast = () => {
      customToast(t('common.pleaseEnterNumbers'));
    };

    const onBlurOTP = (e) => {
      onBlur(e);
      setFocus(false);
    };

    const onChangeOTP = (otp) => {
      onChange(otp);
      // Only set focus to true if OTP is not complete (user is still typing)
      // If OTP is complete, set focus to false to allow errors to show
      if (!isValidSixDigitOTP(otp)) {
        setFocus(true);
        // Clear error when user starts editing (OTP becomes incomplete)
        onClearError && onClearError();
      } else {
        // OTP is complete - set focus to false so errors can display
        // This handles paste scenarios where complete OTP is entered at once
        setFocus(false);
        // Set touched to true so errors can be displayed
        formApi.setTouched(field, true);
      }
      onHandleChange && onHandleChange(otp);

      // Auto-verify if enabled and OTP is valid
      if (enableAutoVerify && isValidSixDigitOTP(otp) && onVerifyOTP) {
        isAutoVerifyingRef.current = true;
        // Set touched to true when auto-verification starts
        formApi.setTouched(field, true);
        onVerifyOTP(otp);
      } else {
        isAutoVerifyingRef.current = false;
      }
    };

    useEffect(() => {
      if (!value) {
        setFocus(true);
        isAutoVerifyingRef.current = false;
        previousErrorRef.current = undefined;
      }
    }, [value]);

    // Handle error display when verification fails (both auto-verify and manual)
    useEffect(() => {
      // Only handle error display for auto-verify scenarios
      if (!enableAutoVerify) {
        return;
      }

      const hasCompleteOTP = typeof value === 'string' && isValidSixDigitOTP(value);
      const hasError = Boolean(error);
      const wasAutoVerifying = isAutoVerifyingRef.current;

      // If error exists and OTP is complete, show the error
      // This handles both auto-verify failures and manual verification failures (including paste)
      if (hasError && hasCompleteOTP) {
        // Show error for complete OTPs with errors
        // This ensures errors from verification failures are displayed (including paste scenarios)
        setFocus(false);
        isAutoVerifyingRef.current = false;
        // Ensure touched is true so error can be displayed
        if (!touched) {
          formApi.setTouched(field, true);
        }
      } else if (!hasError && wasAutoVerifying) {
        // If error cleared and we were auto-verifying, reset the flag
        isAutoVerifyingRef.current = false;
      }

      previousErrorRef.current = error;
    }, [error, value, field, formApi, touched, enableAutoVerify]);


    return render(
      <OtpInputController
        onChange={(value) => {
          onChangeOTP(value);
        }}
        otpValue={value as string}
        isError={Boolean(error) || isNotValid}
        label={label}
        errorMessage={error}
        shouldAutoFocus={true}
        showToast={showToast}
        isDisabled={disabled}
        inputClassName={inputClassName}
        inputFocusClassName={inputFocusClassName}
        errorClassName={errorClassName}
        t={t}
        apiStatus={apiStatus}
        labelStyles={labelStyles}
        otpContainerStyles={otpContainerStyles}
        errorMessageStyles={errorMessageStyles}
        numInputs={numInputs}
        onBlur={onBlurOTP}
        isFocus={isFocus}
      />,
    );
  },
);
